import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Save,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GradeAttemptPage = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchAttempt();
  }, [attemptId]);

  const fetchAttempt = async () => {
    try {
      const response = await axios.get(`${API}/grading/${attemptId}`, {
        headers: getAuthHeader()
      });
      setAttempt(response.data);
      
      // Initialize grading state for pending questions
      const pendingGrades = {};
      response.data.results.forEach((r, i) => {
        if (r.needs_review) {
          pendingGrades[i] = { is_correct: null, points: '' };
        }
      });
      setGrading(pendingGrades);
    } catch (error) {
      console.error('Error fetching attempt:', error);
      toast.error('Failed to load submission');
      navigate('/grading');
    } finally {
      setLoading(false);
    }
  };

  const handleGrade = async (questionIndex) => {
    const grade = grading[questionIndex];
    if (grade.is_correct === null) {
      toast.error('Please select correct or incorrect');
      return;
    }

    const points = grade.is_correct ? 1 : (parseFloat(grade.points) || 0);
    
    setSaving(prev => ({ ...prev, [questionIndex]: true }));

    try {
      await axios.post(`${API}/grading/review`, {
        attempt_id: attemptId,
        question_index: questionIndex,
        is_correct: grade.is_correct,
        points_earned: points
      }, {
        headers: getAuthHeader()
      });

      // Update local state
      setAttempt(prev => {
        const newResults = [...prev.results];
        newResults[questionIndex] = {
          ...newResults[questionIndex],
          is_correct: grade.is_correct,
          points_earned: points,
          needs_review: false
        };
        
        const newTotal = newResults.reduce((sum, r) => sum + r.points_earned, 0);
        const newPercentage = (newTotal / prev.max_score) * 100;
        const stillNeedsReview = newResults.some(r => r.needs_review);
        
        return {
          ...prev,
          results: newResults,
          total_score: newTotal,
          percentage: newPercentage,
          needs_review: stillNeedsReview
        };
      });

      // Remove from grading state
      setGrading(prev => {
        const newState = { ...prev };
        delete newState[questionIndex];
        return newState;
      });

      toast.success('Grade saved');
    } catch (error) {
      console.error('Grading error:', error);
      toast.error('Failed to save grade');
    } finally {
      setSaving(prev => ({ ...prev, [questionIndex]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-48 mb-4" />
          <Skeleton className="h-32 mb-4" />
          <Skeleton className="h-32" />
        </main>
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-xl font-medium text-primary mb-2">Submission not found</h2>
          <Button onClick={() => navigate('/grading')}>Back to Grading</Button>
        </div>
      </div>
    );
  }

  const pendingCount = attempt.results.filter(r => r.needs_review).length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/grading')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-2xl font-medium text-primary">
              {attempt.student_name}'s Submission
            </h1>
            <p className="text-muted-foreground">{attempt.quiz_title}</p>
          </div>
        </motion.div>

        {/* Score Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Current Score</p>
                  <p className="text-4xl font-serif font-bold text-primary">
                    {Math.round(attempt.percentage)}%
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {attempt.total_score} / {attempt.max_score} points
                  </p>
                </div>
                {pendingCount > 0 && (
                  <div className="text-right">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full text-accent">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">{pendingCount} question{pendingCount > 1 ? 's' : ''} to review</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Questions */}
        <div className="space-y-4">
          {attempt.results.map((result, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              <Card className={`border-2 ${
                result.needs_review 
                  ? 'border-accent/50 bg-accent/5' 
                  : result.is_correct 
                  ? 'border-green-200' 
                  : 'border-red-200'
              }`}>
                <CardContent className="p-6">
                  {/* Question Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <span className="text-xs font-medium px-2 py-1 bg-secondary rounded-full text-muted-foreground capitalize">
                        {result.question_type.replace('_', ' ')}
                      </span>
                    </div>
                    {!result.needs_review && (
                      <div className={`flex items-center gap-2 ${result.is_correct ? 'text-green-600' : 'text-red-600'}`}>
                        {result.is_correct ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-semibold">{result.points_earned}/{result.points_possible}</span>
                      </div>
                    )}
                  </div>

                  {/* Question */}
                  <p className="font-medium text-foreground mb-4">{result.question}</p>

                  {/* Answers */}
                  <div className="space-y-3 mb-4">
                    <div className="p-3 bg-secondary/50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Student's Answer</p>
                      <p className="text-foreground">{result.student_answer || '(No answer)'}</p>
                    </div>
                    <div className="p-3 bg-primary/5 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Correct Answer</p>
                      <p className="text-primary font-medium">{result.correct_answer}</p>
                    </div>
                  </div>

                  {/* Grading Interface */}
                  {result.needs_review && grading[index] && (
                    <div className="border-t border-border pt-4 mt-4">
                      <p className="font-medium text-foreground mb-3">Grade this answer:</p>
                      
                      <RadioGroup
                        value={grading[index]?.is_correct?.toString()}
                        onValueChange={(value) => {
                          setGrading(prev => ({
                            ...prev,
                            [index]: { ...prev[index], is_correct: value === 'true' }
                          }));
                        }}
                        className="flex gap-4 mb-4"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="true" id={`correct-${index}`} />
                          <Label htmlFor={`correct-${index}`} className="flex items-center gap-1 cursor-pointer">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            Correct (1 point)
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="false" id={`incorrect-${index}`} />
                          <Label htmlFor={`incorrect-${index}`} className="flex items-center gap-1 cursor-pointer">
                            <XCircle className="w-4 h-4 text-red-600" />
                            Incorrect
                          </Label>
                        </div>
                      </RadioGroup>

                      {grading[index]?.is_correct === false && (
                        <div className="flex items-center gap-3 mb-4">
                          <Label className="text-sm">Partial credit:</Label>
                          <Input
                            type="number"
                            min="0"
                            max="1"
                            step="0.25"
                            value={grading[index]?.points}
                            onChange={(e) => setGrading(prev => ({
                              ...prev,
                              [index]: { ...prev[index], points: e.target.value }
                            }))}
                            className="w-24 h-10"
                            placeholder="0"
                          />
                          <span className="text-sm text-muted-foreground">/ 1 point</span>
                        </div>
                      )}

                      <Button
                        onClick={() => handleGrade(index)}
                        disabled={saving[index] || grading[index]?.is_correct === null}
                        className="gap-2"
                        data-testid={`save-grade-${index}`}
                      >
                        {saving[index] ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Save Grade
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <Button
            variant="outline"
            onClick={() => navigate('/grading')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Grading
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GradeAttemptPage;
